import * as R from 'ramda';
import React from 'react';
import {
  pure,
  compose,
  withState,
  withHandlers,
} from 'react-recompose';
// components
import { TextComponent } from '../../../components/text';
// features
import PC from '../../permission/role-permission';
import { AuthWrapper } from '../../permission/index';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import { withFixedPopover, withSetExpandedContainerOptions } from '../../../hocs';
// icons
import * as I from '../../../svgs';
// ui
import {
  Box,
  Flex,
  AbsoluteBox,
  RelativeBox,
} from '../../../ui';
// feature
import InvoiceDetailsActions from './invoice-details-actions';
import { getRowOptions } from '../settings/invoice-details-settings';
//////////////////////////////////////////////////

const darkBlue = G.getTheme('colors.dark.blue');
const lightBlue = G.getTheme('colors.light.blue');
const mainLight = G.getTheme('colors.light.mainLight');

const enhance = compose(
  withFixedPopover,
  withSetExpandedContainerOptions,
  withState('expanded', 'toggle', false),
  withHandlers({
    handleClickTel: ({ telGuid, handleSetExpandedContainerOptions }) => () =>
      handleSetExpandedContainerOptions({ visitPageGuid: telGuid, componentType: GC.PAGE_DISPATCH_DETAILS_NEW_LOAD }),
    handleClickMoreIcon: (props) => (e) => (
      props.openFixedPopup({
        position: 'right',
        el: e.currentTarget,
        content: (
          <InvoiceDetailsActions
            edit={props.edit}
            completed={props.completed}
            closeFixedPopup={props.closeFixedPopup}
            remove={() => props.remove(props.guid, props.invoiceNumber)}
          />
        ),
      })
    ),
  }),
  pure,
);

const renderOptions = (options) => options.map((option, i) => (
  <TextComponent
    title={option.text}
    withEllipsis={true}
    {...option.textProps}
    key={`${option.text}-${i}`}
  >
    {option.text}
  </TextComponent>
));

export const InfoRow = (props) => {
  if (G.isNilOrEmpty(R.path(['options', 1, 'text'], props))) return null;

  if (R.and(G.isNotNil(props.clickHandler), R.not(props.actionsDisabled))) {
    return (
      <Flex
        width='100%'
        fontSize={11}
        cursor='pointer'
        color={lightBlue}
        mb={R.or(props.mb, '6px')}
        onClick={props.clickHandler}
        justifyContent='space-between'
      >
        {renderOptions(props.options)}
      </Flex>
    );
  }

  return (
    <Flex
      width='100%'
      fontSize={11}
      mb={R.or(props.mb, '6px')}
      justifyContent='space-between'
    >
      {renderOptions(props.options)}
    </Flex>
  );
};

const PayrollInvoiceDetails = (props) => {
  const {
    total,
    toggle,
    expanded,
    completed,
    actionsDisabled,
    handleClickMoreIcon,
  } = props;

  const currency = G.getCurrencySymbol(props.currency);
  const rowOptions = getRowOptions(props);

  return (
    <RelativeBox
      mr={15}
      mb={15}
      width={360}
      borderRadius={5}
      overflow='hidden'
      bg={G.getTheme('colors.light.lightGrey')}
    >
      <Flex
        width='100%'
        p='3px 10px'
        bg={darkBlue}
        color={mainLight}
      >
        <Box width='50%'>
          <InfoRow mb='0px' options={rowOptions.invoiceNumber} />
        </Box>
        <Box width='50%'>
          <InfoRow mb='0px'options={rowOptions.status} />
        </Box>
      </Flex>
      <Flex width='100%' p={10} height={90}>
        <Flex width='50%' flexDirection='column'>
          <InfoRow
            options={rowOptions.tel}
            actionsDisabled={actionsDisabled}
            clickHandler={props.handleClickTel}
          />
          <InfoRow options={rowOptions.invoiceDate} />
          <InfoRow options={rowOptions.paymentDueDate} />
          <InfoRow options={G.getPropFromObject(GC.FIELD_GL_CODE, rowOptions)} />
        </Flex>
        <Flex width='50%' flexDirection='column' name='COLUMN'>
          <InfoRow options={rowOptions.transportationMode} />
          <InfoRow options={rowOptions.totalTripDistance} />
          <InfoRow options={rowOptions.totalTripWeight} />
          <Flex width='100%' fontSize={11}>
            <TextComponent
              mr={10}
              title={'Total:'}
              withEllipsis={true}
              width='calc(45% - 10px)'
            >
              {G.getWindowLocale('titles:total', 'Total')}:
            </TextComponent>
            {
              G.isNotNil(total) &&
              <TextComponent
                p='4px'
                bg={lightBlue}
                fontWeight={700}
                borderRadius={3}
                color={mainLight}
                withEllipsis={true}
                display='inline-block'
                maxWidth='calc(55% - 10px)'
                title={`${currency} ${total}`}
              >
                {`${currency} ${G.toFixed(total)}`}
              </TextComponent>
            }
          </Flex>
        </Flex>
      </Flex>
      <Box
        overflow='hidden'
        borderTop={G.ifElse(
          expanded,
          '2px solid',
          'none',
        )}
        height={G.ifElse(expanded, 'auto', '0px')}
        borderColor={G.getTheme('colors.lightGrey')}
      >
        <Flex width='100%' p='10px 10px 0 10px' flexDirection='column'>
          <InfoRow options={rowOptions.lineHaul} />
          <InfoRow options={rowOptions.fuel} />
          <InfoRow mb={10} options={rowOptions.otherCharges} />
          <InfoRow options={rowOptions.comments} />
        </Flex>
      </Box>
      <Flex
        height={10}
        width='100%'
        bg={darkBlue}
        justifyContent='center'
        onClick={() => toggle(R.not(expanded))}
      >
        {
          G.ifElse(
            expanded,
            I.arrowUpSimple,
            I.arrowDownSimple,
          )(mainLight)
        }
      </Flex>
      {
        R.not(actionsDisabled) &&
        <AuthWrapper
          has={G.ifElse(
            G.isTrue(completed),
            [PC.TEL_INVOICE_OVERWRITE_EXECUTE],
            [PC.TEL_FLEET_INVOICE_WRITE, PC.TEL_INVOICE_OVERWRITE_EXECUTE],
          )}
        >
          <AbsoluteBox top={28} right='8px'>
            <RelativeBox px='8px' zIndex={10} cursor='pointer' onClick={handleClickMoreIcon}>
              {I.threeDots(darkBlue, null, 15)}
            </RelativeBox>
          </AbsoluteBox>
        </AuthWrapper>
      }
    </RelativeBox>
  );
};

export default enhance(PayrollInvoiceDetails);
